import { createContext, useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { SkeletonTheme } from "react-loading-skeleton";

interface Props {
  children: React.ReactNode | React.ReactNode[];
}

interface IThemeContext {
  theme: string | null;
  toggleTheme: (theme: string) => void;
}

export const ThemeContext = createContext<IThemeContext>({
  theme: "dark",
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }: Props) => {
  const { pathname } = useLocation();

  const [theme, setTheme] = useState("");

  const toggleTheme = (theme: string) => {
    const page = document.querySelector("html");
    page?.classList.remove("light", "dark");
    if (theme === "light") {
      page?.classList.add("light");
      if (page) {
        page.style.colorScheme = "light";
      }
      localStorage.setItem("theme", "light");
      setTheme("light");
    } else if (theme === "dark") {
      page?.classList.add("dark");
      if (page) {
        page.style.colorScheme = "dark";
      }
      localStorage.setItem("theme", "dark");
      setTheme("dark");
    }
  };

  useEffect(() => {
    const exceptions = [
      "/login",
      "/signup",
      "/password-recovery",
      "/reset-password",
      "/billing",
    ];

    const doc = document.querySelector("html");
    if (exceptions.includes(pathname)) {
      doc?.classList.remove("dark");
      setTheme("light");
    } else {
      const prefersDarkScheme = window.matchMedia(
        "(prefers-color-scheme: dark)"
      );
      const currentTheme = localStorage.getItem("theme");
      doc?.classList.remove("light", "dark");
      if (currentTheme === "dark") {
        doc?.classList.add("dark");
        setTheme("dark");
        if (doc) {
          doc.style.colorScheme = "dark";
        }
      } else if (currentTheme === "light") {
        doc?.classList.add("light");
        setTheme("light");
        if (doc) {
          doc.style.colorScheme = "light";
        }
      } else if (prefersDarkScheme.matches) {
        doc?.classList.add("dark");
        setTheme("dark");
        if (doc) {
          doc.style.colorScheme = "dark";
        }
      }
    }
  }, [pathname]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <SkeletonTheme
        baseColor={theme === "dark" ? "#2E2E30" : "#ebebeb"}
        highlightColor={theme === "dark" ? "#3d3e40" : "#f5f5f5"}
      >
        {children}
      </SkeletonTheme>
    </ThemeContext.Provider>
  );
};

export default function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used whithin an app");
  }

  return context;
}
